import React, { useEffect, useRef, useMemo, useState } from 'react';
import { Icon, mergeStyleSets } from '@fluentui/react';
import { ArrowLeft, Database, Plus, FilePdf, FloppyDisk, Check, SquaresFour, Gauge, Hash, ChartBar, Table } from '@phosphor-icons/react';
import { GridLayout, useContainerWidth } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import DashboardWidgetCard from './DashboardWidgetCard';
import DashboardFilterBar from './DashboardFilterBar';
import type { DashboardWidget, DashboardFilter, SuggestionGroup, WidgetType } from '../../types/dashboard.types';
import type { ChartPanelChartType } from '../chat/ChartPanel';
import type { Layout, LayoutItem } from 'react-grid-layout';

const ROW_HEIGHT = 80;
const COLS = 12;

function defaultH(widget: DashboardWidget): number {
  if (widget.type === 'kpi' || widget.type === 'number') return 2;
  if (widget.type === 'table') return 5;
  return 4; // charts: 4 rows = 320px → matches ~280px chart + header
}

function sizeToW(size: string): number {
  if (size === 'small') return 4;
  if (size === 'large') return 12;
  return 6;
}

/**
 * Build the react-grid-layout Layout array from widgets.
 * If a widget has no gridX/gridY, compute a packed position
 * rather than relying on Infinity (which causes single-column stacking).
 */
function widgetsToLayout(widgets: DashboardWidget[]): LayoutItem[] {
  // Build occupancy grid: track the highest Y+H for each column
  const colBottom = new Array(COLS).fill(0);

  // First pass: account for widgets that already have positions
  const positioned: LayoutItem[] = [];
  const unpositioned: { idx: number; w: number; h: number }[] = [];

  widgets.forEach((widget, idx) => {
    const w = widget.gridW ?? sizeToW(widget.size);
    const h = widget.gridH ?? defaultH(widget);

    if (widget.gridX !== undefined && widget.gridY !== undefined) {
      positioned.push({ i: widget.id, x: widget.gridX, y: widget.gridY, w, h });
      // Mark columns as occupied
      for (let c = widget.gridX; c < Math.min(widget.gridX + w, COLS); c++) {
        colBottom[c] = Math.max(colBottom[c], widget.gridY + h);
      }
    } else {
      unpositioned.push({ idx, w, h });
    }
  });

  // Second pass: pack unpositioned widgets into available space
  for (const item of unpositioned) {
    const widget = widgets[item.idx];
    const { w: iw, h: ih } = item;

    // Find the first row where a span of `iw` columns is available
    let bestX = 0;
    let bestY = Infinity;

    for (let x = 0; x <= COLS - iw; x++) {
      // The Y needed if we place at column x is the max of colBottom[x..x+iw-1]
      let rowY = 0;
      for (let c = x; c < x + iw; c++) {
        rowY = Math.max(rowY, colBottom[c]);
      }
      if (rowY < bestY) {
        bestY = rowY;
        bestX = x;
      }
    }

    positioned.push({ i: widget.id, x: bestX, y: bestY, w: iw, h: ih });
    // Update occupancy
    for (let c = bestX; c < bestX + iw; c++) {
      colBottom[c] = Math.max(colBottom[c], bestY + ih);
    }
  }

  return positioned;
}

interface Props {
  dashboardName: string;
  onNameChange: (name: string) => void;
  widgets: DashboardWidget[];
  datasetNames: string[];
  filters?: DashboardFilter[];
  onFilterChange?: (filterId: string, value: string) => void;
  onRemoveFilter?: (filterId: string) => void;
  onRemoveWidget: (id: string) => void;
  onResizeWidget: (id: string, size: 'small' | 'medium' | 'large') => void;
  onChangeChartType: (id: string, chartType: ChartPanelChartType) => void;
  onChangeWidgetType?: (id: string, newType: WidgetType) => void;
  onUpdateLegendLabels?: (id: string, legendLabels: Record<string, string>) => void;
  onLayoutChange: (layout: Layout) => void;
  onSave: () => void;
  saving: boolean;
  justSaved?: boolean;
  onExportPdf?: () => void;
  exporting?: boolean;
  onClose: () => void;
  onNewDashboard: () => void;
  suggestionChips?: string[];
  suggestionGroups?: SuggestionGroup[];
  onSuggestionClick?: (s: string) => void;
}

const s = mergeStyleSets({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    minWidth: 0,
    overflow: 'auto',
    // Dot-grid canvas — communicates "design surface" without being loud
    backgroundColor: '#EAEBEE',
    backgroundImage: 'radial-gradient(circle, #C4C5CA 1px, transparent 1px)',
    backgroundSize: '26px 26px',
  },
  headerBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 20px',
    height: 52,
    background: '#FFFFFF',
    borderBottom: '1px solid #E8E8E8',
    flexShrink: 0,
    gap: 12,
    flexWrap: 'wrap' as const,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  nameInput: {
    fontSize: 15,
    fontWeight: 700,
    color: '#232323',
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: 4,
    padding: '4px 8px',
    minWidth: 180,
    maxWidth: 320,
    outline: 'none',
    transition: 'border-color 0.12s ease',
    selectors: {
      ':hover': { borderColor: '#E1E1E1' },
      ':focus': { borderColor: '#2e873d' },
    },
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  datasetChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 10px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 500,
    background: '#d9f4d7',
    color: '#2e873d',
    border: '1px solid rgba(46,135,61,0.2)',
  },
  widgetCount: {
    fontSize: 12,
    color: '#747474',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  headerIconBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: 4,
    border: 'none',
    background: 'transparent',
    color: '#747474',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'all 0.12s ease',
    selectors: {
      ':hover': { background: '#F5F5F5', color: '#232323' },
    },
  },
  newBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '6px 12px',
    borderRadius: 4,
    background: 'transparent',
    color: '#747474',
    border: '1px solid #E1E1E1',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.12s ease',
    flexShrink: 0,
    selectors: {
      ':hover': { background: '#F5F5F5', color: '#232323', borderColor: '#C0C0C0' },
    },
  },
  saveBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 16px',
    borderRadius: 4,
    background: '#2e873d',
    color: '#FFFFFF',
    border: 'none',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.12s ease',
    selectors: {
      ':hover': { background: '#256d31' },
      ':disabled': { opacity: 0.6, cursor: 'default' },
    },
  },
  gridWrap: {
    padding: 20,
    flex: 1,
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    textAlign: 'center' as const,
  },
  emptyIcon: {
    width: 64,
    height: 64,
    borderRadius: 16,
    background: '#d9f4d7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#232323',
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 13,
    color: '#747474',
    maxWidth: 360,
    lineHeight: '1.6',
    marginBottom: 20,
  },
  stepRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0,
    marginBottom: 24,
  },
  stepItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  stepNumber: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: '#2e873d',
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
    flexShrink: 0,
  },
  stepLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: '#484848',
    whiteSpace: 'nowrap' as const,
  },
  stepArrow: {
    margin: '0 12px',
    color: '#C0C0C0',
    fontSize: 12,
  },
  suggestionGroupTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    fontWeight: 600,
    color: '#484848',
    marginTop: 16,
    marginBottom: 6,
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 8,
    justifyContent: 'center',
  },
  suggestionChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '6px 14px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 500,
    background: '#FFFFFF',
    color: '#2e873d',
    border: '1px solid #2e873d',
    cursor: 'pointer',
    transition: 'all 0.12s ease',
    selectors: {
      ':hover': {
        background: '#2e873d',
        color: '#FFFFFF',
      },
    },
  },
  suggestionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 10,
  },
  suggestionCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 4,
    textAlign: 'left' as const,
    background: '#FFFFFF',
    border: '1px solid #E4E4E8',
    borderRadius: 10,
    padding: '12px 14px',
    cursor: 'pointer',
    transition: 'all 0.14s ease',
    selectors: {
      ':hover': {
        borderColor: '#2e873d',
        boxShadow: '0 2px 10px rgba(46,135,61,0.12)',
        transform: 'translateY(-1px)',
      },
    },
  },
  suggestionCardText: {
    fontSize: 12.5,
    fontWeight: 500,
    color: '#232323',
    lineHeight: '1.4',
  },
  typeLegend: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
    marginBottom: 20,
  },
  typeLegendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    color: '#484848',
  },
  tabBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    padding: '0 20px',
    background: '#FFFFFF',
    borderBottom: '1px solid #E1E1E1',
    flexShrink: 0,
    overflowX: 'auto' as const,
  },
  tab: {
    padding: '10px 20px',
    fontSize: 13,
    fontWeight: 500,
    color: '#747474',
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    transition: 'all 0.12s ease',
    selectors: {
      ':hover': { color: '#2e873d' },
    },
  },
  tabActive: {
    color: '#2e873d',
    fontWeight: 600,
    borderBottomColor: '#2e873d',
  },
});

// Global CSS for react-grid-layout resize handle (high-specificity selectors)
const resizeHandleCSS = `
.react-grid-item > .react-resizable-handle {
  position: absolute;
  width: 20px;
  height: 20px;
  bottom: 0;
  right: 0;
  cursor: se-resize;
  z-index: 20;
}
.react-grid-item > .react-resizable-handle::after {
  content: '';
  position: absolute;
  right: 4px;
  bottom: 4px;
  width: 8px;
  height: 8px;
  border-right: 2px solid #C0C0C0;
  border-bottom: 2px solid #C0C0C0;
  border-radius: 0 0 2px 0;
  transition: border-color 0.15s ease;
}
.react-grid-item > .react-resizable-handle:hover::after {
  border-color: #2e873d;
}
`;

export default function DashboardCanvas({
  dashboardName,
  onNameChange,
  widgets,
  datasetNames,
  filters,
  onFilterChange,
  onRemoveFilter,
  onRemoveWidget,
  onResizeWidget,
  onChangeChartType,
  onChangeWidgetType,
  onUpdateLegendLabels,
  onLayoutChange,
  onSave,
  saving,
  justSaved,
  onExportPdf,
  exporting,
  onClose,
  onNewDashboard,
  suggestionChips,
  suggestionGroups,
  onSuggestionClick,
}: Props) {
  // Tab navigation
  const tabs = useMemo(() => {
    const ids = widgets.map((w) => w.tabId).filter(Boolean) as string[];
    return [...new Set(ids)];
  }, [widgets]);
  const hasTabs = tabs.length > 0;
  const [activeTab, setActiveTab] = useState<string | null>(null);

  // Default to first tab when tabs appear
  useEffect(() => {
    if (hasTabs && (!activeTab || !tabs.includes(activeTab))) {
      setActiveTab(tabs[0]);
    }
  }, [hasTabs, tabs, activeTab]);

  // Tab label mapping
  const tabLabels: Record<string, string> = {
    budget: 'Budget & Expenditure',
    costcentres: 'Cost Centres',
    projects: 'Projects',
    purchaseorders: 'Purchase Orders',
  };

  // Filter widgets by active tab (backward compatible: no tabs → show all)
  const visibleWidgets = useMemo(() => {
    if (!hasTabs) return widgets;
    return widgets.filter((w) => w.tabId === activeTab);
  }, [widgets, hasTabs, activeTab]);

  const hasWidgets = widgets.length > 0;
  const layout = useMemo(() => widgetsToLayout(visibleWidgets), [visibleWidgets]);
  const { width, containerRef, mounted } = useContainerWidth();

  // Persist computed positions back to widget state for widgets that lack them.
  // This ensures drag works correctly (no Infinity resets on re-render).
  const syncedRef = useRef<string>('');
  useEffect(() => {
    const unpositioned = widgets.filter((w) => w.gridX === undefined || w.gridY === undefined);
    if (unpositioned.length === 0) return;
    // Avoid re-syncing the same set
    const key = unpositioned.map((w) => w.id).join(',');
    if (key === syncedRef.current) return;
    syncedRef.current = key;
    onLayoutChange(layout);
  }, [widgets, layout, onLayoutChange]);

  return (
    <div className={s.root}>
      <style>{resizeHandleCSS}</style>
      {/* Header bar */}
      <div className={s.headerBar}>
        <div className={s.headerLeft}>
          <button className={s.headerIconBtn} onClick={onClose} title="Close dashboard">
            <ArrowLeft size={14} weight="regular" />
          </button>
          <input
            className={s.nameInput}
            value={dashboardName}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Untitled Dashboard"
          />
        </div>
        <div className={s.headerRight}>
          {datasetNames.map((ds) => (
            <span key={ds} className={s.datasetChip}>
              <Database size={11} weight="regular" />
              {ds}
            </span>
          ))}
          {hasWidgets && (
            <span className={s.widgetCount}>
              {hasTabs ? `${visibleWidgets.length} of ${widgets.length} widgets` : `${widgets.length} widget${widgets.length !== 1 ? 's' : ''}`}
            </span>
          )}
          <button className={s.newBtn} onClick={onNewDashboard} title="Start a new dashboard">
            <Plus size={12} weight="regular" />
            New
          </button>
          {onExportPdf && hasWidgets && (
            <button className={s.saveBtn} onClick={onExportPdf} disabled={exporting} style={{ background: '#484848' }}>
              <FilePdf size={13} weight="regular" />
              {exporting ? 'Exporting...' : 'Export PDF'}
            </button>
          )}
          <button
            className={s.saveBtn}
            onClick={onSave}
            disabled={saving || justSaved}
            style={justSaved ? { background: '#2e873d' } : undefined}
          >
            {justSaved ? <Check size={13} weight="bold" /> : <FloppyDisk size={13} weight="regular" />}
            {saving ? 'Saving...' : justSaved ? 'Saved' : 'Save'}
          </button>
        </div>
      </div>

      {/* Tab bar */}
      {hasTabs && (
        <div className={s.tabBar}>
          {tabs.map((tab) => (
            <button
              key={tab}
              className={`${s.tab} ${activeTab === tab ? s.tabActive : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tabLabels[tab] || tab}
            </button>
          ))}
        </div>
      )}

      {/* Filter bar */}
      {filters && filters.length > 0 && onFilterChange && onRemoveFilter && (
        <DashboardFilterBar
          filters={filters}
          onFilterChange={onFilterChange}
          onRemoveFilter={onRemoveFilter}
        />
      )}

      {/* Content */}
      {hasWidgets ? (
        <div className={s.gridWrap} ref={containerRef as React.RefObject<HTMLDivElement>}>
          {mounted && (
            <GridLayout
              width={width}
              layout={layout}
              gridConfig={{ cols: 12, rowHeight: ROW_HEIGHT, margin: [16, 16] as const, containerPadding: [0, 0] as const, maxRows: Infinity }}
              dragConfig={{ enabled: true, handle: '.widget-drag-handle', bounded: false, threshold: 3 }}
              resizeConfig={{ enabled: true, handles: ['se'] }}
              onDragStop={(newLayout) => onLayoutChange(newLayout)}
              onResizeStop={(newLayout) => onLayoutChange(newLayout)}
            >
              {visibleWidgets.map((widget) => (
                <DashboardWidgetCard
                  key={widget.id}
                  widget={widget}
                  onRemove={onRemoveWidget}
                  onResize={onResizeWidget}
                  onChangeChartType={onChangeChartType}
                  onChangeWidgetType={onChangeWidgetType}
                  onUpdateLegendLabels={onUpdateLegendLabels}
                />
              ))}
            </GridLayout>
          )}
        </div>
      ) : (
        <div className={s.emptyState}>
          <div className={s.emptyIcon}>
            <SquaresFour size={28} color="#2e873d" weight="regular" />
          </div>
          <div className={s.emptyTitle}>Let's build your dashboard</div>
          <div className={s.emptySubtitle}>
            Use the chat panel on the right to describe what you'd like to see.
            I'll create KPIs, charts and tables for you.
          </div>

          {/* Widget type legend */}
          <div className={s.typeLegend}>
            <div className={s.typeLegendItem}>
              <Gauge size={14} color="#2e873d" weight="regular" />
              KPI numbers
            </div>
            <div className={s.typeLegendItem}>
              <Hash size={14} color="#2e873d" weight="regular" />
              Number cards
            </div>
            <div className={s.typeLegendItem}>
              <ChartBar size={14} color="#2e873d" weight="regular" />
              Charts (bar, line, pie, donut, area)
            </div>
            <div className={s.typeLegendItem}>
              <Table size={14} color="#2e873d" weight="regular" />
              Data tables
            </div>
          </div>

          {/* 3-step indicator */}
          <div className={s.stepRow}>
            <div className={s.stepItem}>
              <div className={s.stepNumber}>1</div>
              <span className={s.stepLabel}>Ask a question</span>
            </div>
            <span className={s.stepArrow}>&#8594;</span>
            <div className={s.stepItem}>
              <div className={s.stepNumber}>2</div>
              <span className={s.stepLabel}>I create the widget</span>
            </div>
            <span className={s.stepArrow}>&#8594;</span>
            <div className={s.stepItem}>
              <div className={s.stepNumber}>3</div>
              <span className={s.stepLabel}>Keep customising</span>
            </div>
          </div>

          {/* Starter suggestions — 2-column card grid */}
          {suggestionGroups && suggestionGroups.length > 0 && (
            <div style={{ maxWidth: 540, width: '100%' }}>
              {suggestionGroups.map((group) => (
                <div key={group.category}>
                  <div className={s.suggestionGroupTitle}>
                    <Icon iconName={group.icon} styles={{ root: { fontSize: 13, color: '#2e873d' } }} />
                    {group.category}
                  </div>
                  <div className={s.suggestionGrid}>
                    {group.suggestions.map((chip) => (
                      <button
                        key={chip}
                        className={s.suggestionCard}
                        onClick={() => onSuggestionClick?.(chip)}
                      >
                        <span className={s.suggestionCardText}>{chip}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Connected datasets at bottom */}
          {datasetNames.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 11, color: '#747474', marginBottom: 8 }}>Connected datasets</div>
              <div className={s.chipRow}>
                {datasetNames.map((ds) => (
                  <span key={ds} className={s.datasetChip}>
                    <Database size={11} weight="regular" />
                    {ds}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
